import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUp, Check, Globe } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useAnimatedNavigate } from "@/hooks/useAnimatedNavigate";
import { DELAYS } from '@/constants/timing';
import { useUserContext } from "@/contexts/UserContext";
import { cn } from "@/lib/utils";
import { useLocalizations } from "@/hooks/useLocalizations";
import { useTranslation } from "react-i18next";
import { LPages } from "@/components/i18n";
import { useAnalyticsTracking } from "@/hooks/useAnalyticsTracking";
import { supabase } from "@/integrations/supabase/client";
import { useLocationNavigation } from "@/hooks/useLocationNavigation";
import { LocationPicker } from "@/components/shared/LocationPicker";
import { INTERNATIONAL_LOCATION_ID } from "@/constants/config";
import { routeTo, buildRouteWithQuery } from "@/lib/routeHelpers";
import { useLocalizedRoutes } from "@/hooks/useLocalizedRoutes";
import { ChatFileUpload, DocumentChip } from "@/components/tax-oracle/ChatFileUpload";
import type { UploadedDocument } from "@/components/tax-oracle/ChatFileUpload";

interface HeroChatProps {
  preselectedLocation?: string; // location_id
  className?: string;
}

export function HeroChat({ preselectedLocation, className }: HeroChatProps) {
  const navigate = useAnimatedNavigate();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { lPages, lCommon } = useLocalizations();
  const { ready: translationsReady, i18n } = useTranslation('pages', { useSuspense: false });
  const [chatText, setChatText] = useState("");
  const [placeholder, setPlaceholder] = useState("");
  const [attachments, setAttachments] = useState<UploadedDocument[]>([]);
  const { intentMode, setIntentMode, targetLocation, detectedLocation } = useUserContext();
  const { selectLocation, selectInternational, currentLocation } = useLocationNavigation();
  const { trackHeroChatSubmit } = useAnalyticsTracking();
  const { getPath } = useLocalizedRoutes();
  
  const internationalPlanning = intentMode === 'international';

  const handleLocationSelect = (locationId: string) => {
    if (locationId === INTERNATIONAL_LOCATION_ID || locationId === 'GLOBAL') {
      selectInternational({ skipNavigation: true });
    } else {
      selectLocation(locationId, { skipNavigation: true });
    }
  };

  // URL location sync removed - UserContext is the single source of truth
  // Location is now only changed via explicit user action (dropdown selection)

  // Compute translated strings on every render — useTranslation triggers
  // re-renders when new resources load (bindI18nStore: 'added'), so removing
  // useMemo ensures we pick up PT-BR values as soon as the JSON is fetched.
  const translatedStrings = (() => {
    if (!translationsReady) return null;
    
    const staticText = lPages('home.hero.placeholder.static', { defaultValue: 'Best tax strategies to' });
    
    // Guard: ensure translations are actually loaded
    if (!staticText || staticText === 'home.hero.placeholder.static') {
      return null;
    }
    
    return {
      staticText,
      dynamicOptions: [
        lPages('home.hero.placeholder.dynamic_1', { defaultValue: 'protect client wealth?' }),
        lPages('home.hero.placeholder.dynamic_2', { defaultValue: 'reduce tax liability?' }),
        lPages('home.hero.placeholder.dynamic_3', { defaultValue: 'optimize business structure?' }),
        lPages('home.hero.placeholder.dynamic_4', { defaultValue: 'minimize inheritance taxes?' }),
        lPages('home.hero.placeholder.dynamic_5', { defaultValue: 'structure international assets?' }),
        lPages('home.hero.placeholder.dynamic_6', { defaultValue: 'plan for retirement abroad?' }),
        lPages('home.hero.placeholder.dynamic_7', { defaultValue: 'navigate cross-border taxation?' }),
        lPages('home.hero.placeholder.dynamic_8', { defaultValue: 'establish tax residency?' }),
      ],
    };
  })();

  // Stable key for animation restart — only changes when actual text changes (language switch)
  const translationKey = translatedStrings
    ? translatedStrings.staticText + '|' + translatedStrings.dynamicOptions[0]
    : '';

  // Animation state stored in refs to prevent restart on re-renders
  const animationStateRef = useRef({
    currentDynamicIndex: 0,
    currentCharIndex: 0,
    phase: 'typing-static' as 'typing-static' | 'typing-dynamic' | 'pausing' | 'deleting-dynamic',
  });
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelledRef = useRef(false);

  // Animated placeholder effect with 1 static + 8 dynamic values
  useEffect(() => {
    // Don't start animation until translations are ready
    if (!translatedStrings) return;
    
    const { staticText, dynamicOptions } = translatedStrings;
    const state = animationStateRef.current;
    
    // Reset state for new locale
    state.currentDynamicIndex = 0;
    state.currentCharIndex = 0;
    state.phase = 'typing-static';
    cancelledRef.current = false;

    const TYPING_SPEED = 80;
    const DELETING_SPEED = 30;
    const PAUSE_AFTER_COMPLETE = 2000;
    const PAUSE_BEFORE_NEXT = 300;

    const animate = () => {
      if (cancelledRef.current) return;
      
      switch (state.phase) {
        case 'typing-static':
          state.currentCharIndex++;
          setPlaceholder(staticText.substring(0, state.currentCharIndex));
          
          if (state.currentCharIndex >= staticText.length) {
            state.phase = 'typing-dynamic';
            state.currentCharIndex = 0;
            timeoutRef.current = setTimeout(animate, TYPING_SPEED);
          } else {
            timeoutRef.current = setTimeout(animate, TYPING_SPEED);
          }
          break;

        case 'typing-dynamic': {
          const currentDynamic = dynamicOptions[state.currentDynamicIndex].toLowerCase();
          state.currentCharIndex++;
          // Add space between static and dynamic
          setPlaceholder(staticText + ' ' + currentDynamic.substring(0, state.currentCharIndex));
          
          if (state.currentCharIndex >= currentDynamic.length) {
            state.phase = 'pausing';
            timeoutRef.current = setTimeout(animate, PAUSE_AFTER_COMPLETE);
          } else {
            timeoutRef.current = setTimeout(animate, TYPING_SPEED);
          }
          break;
        }

        case 'pausing':
          state.phase = 'deleting-dynamic';
          timeoutRef.current = setTimeout(animate, PAUSE_BEFORE_NEXT);
          break;

        case 'deleting-dynamic': {
          const deletingDynamic = dynamicOptions[state.currentDynamicIndex].toLowerCase();
          state.currentCharIndex--;
          
          if (state.currentCharIndex <= 0) {
            // Move to next dynamic option
            state.currentDynamicIndex = (state.currentDynamicIndex + 1) % dynamicOptions.length;
            state.phase = 'typing-dynamic';
            state.currentCharIndex = 0;
            setPlaceholder(staticText + ' ');
            timeoutRef.current = setTimeout(animate, PAUSE_BEFORE_NEXT);
          } else {
            setPlaceholder(staticText + ' ' + deletingDynamic.substring(0, state.currentCharIndex));
            timeoutRef.current = setTimeout(animate, DELETING_SPEED);
          }
          break;
        }
      }
    };

    // Start animation after a short delay
    timeoutRef.current = setTimeout(animate, DELAYS.DEBOUNCE_SLOW);

    return () => {
      cancelledRef.current = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [translationKey]);

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = e.target;
    setChatText(textarea.value);
    textarea.style.height = "auto";
    textarea.style.height = textarea.scrollHeight + "px";
  };

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    const trimmedText = chatText.trim();
    if (trimmedText.length === 0 || isSubmitting) return;
    
    setIsSubmitting(true);
    trackHeroChatSubmit(trimmedText, currentLocation || undefined);
    
    
    try {
      // Get current user for session ownership
      const { data: { user } } = await supabase.auth.getUser();
      
      // For anonymous users, skip DB insert and navigate with chat_text param
      // The chat page will create a local session and start streaming
      if (!user?.id) {
        const chatPath = getPath('chat');
        navigate(buildRouteWithQuery(chatPath, { chat_text: trimmedText }));
        setIsSubmitting(false);
        return;
      }
      
      // Create session with user_id and first message - DB is single source of truth
      const sessionKey = `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      const initialMessage = { role: 'user', content: trimmedText };
      
      const { data } = await supabase
        .from('ai_chat_sessions')
        .insert({ 
          session_key: sessionKey, 
          messages: [initialMessage], 
          metadata: { title: null },
          user_id: user.id,
        })
        .select()
        .single();
      
      if (data?.id) {
        // Navigate to chat - TaxOracleChatArea will handle streaming after mount
        navigate(routeTo.chatSession(data.id));
      } else {
        // Fallback: navigate with chat_text param
        const chatPath = getPath('chat');
        navigate(buildRouteWithQuery(chatPath, { chat_text: trimmedText }));
      }
    } catch {
      // Fallback on error
      const chatPath = getPath('chat');
      navigate(buildRouteWithQuery(chatPath, { chat_text: trimmedText }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };


  return (
    <Card 
      className={cn("shadow-lg bg-card w-full max-w-2xl mx-auto", className)}
      style={{ viewTransitionName: 'chat-input-card' }}
    >
      <CardContent className="!p-2 !pb-2 flex flex-col min-h-[160px] text-left">
        {/* Attached document chips */}
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-1.5 px-2 pb-2">
            {attachments.map((doc, i) => (
              <DocumentChip
                key={`${doc.fileName}-${i}`}
                document={doc}
                onRemove={() => setAttachments(prev => prev.filter((_, idx) => idx !== i))}
                compact
              />
            ))}
          </div>
        )}
        <div className="relative flex-1 min-h-[80px]">
          {/* Pseudo-placeholder overlay - supports multi-line wrapping */}
          {!chatText && (
            <div 
              className="absolute top-0 left-0 right-0 p-2 text-lg md:text-xl leading-snug text-muted-foreground pointer-events-none select-none whitespace-pre-wrap break-words"
              aria-hidden="true"
            >
              {placeholder}
            </div>
          )}
          <Textarea
            ref={textareaRef}
            value={chatText}
            onChange={handleTextareaChange}
            onKeyPress={handleKeyPress}
            className="relative z-10 min-h-[80px] w-full resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-lg md:text-xl leading-snug bg-transparent p-2"
          />
        </div>
        
        <div className="flex items-end justify-between gap-2 mt-auto pt-1">
          {/* Left side: Location + International pills */}
          <div className="flex items-center gap-1.5">
            {/* Document Upload */}
            <ChatFileUpload
              onDocumentUploaded={(doc) => setAttachments(prev => [...prev, doc])}
              disabled={isSubmitting}
            />

            {/* Location Picker */}
            <LocationPicker
              value={currentLocation || INTERNATIONAL_LOCATION_ID}
              onSelect={handleLocationSelect}
              variant="ghost"
              size="icon"
              align="start"
              showName={false}
              customTrigger={
                <Button
                  variant="ghost"
                  aria-label={lCommon('aria.select_location', { defaultValue: 'Select Location' })}
                  className="h-8 w-8 p-0 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 active:bg-muted transition-colors"
                >
                  <Globe className="h-4 w-4" />
                </Button>
              }
            />

            {/* International Toggle */}
            <Button
              variant="outline"
              onClick={() => setIntentMode(internationalPlanning ? 'local' : 'international')}
              className={cn(
                "h-7 px-2.5 ml-1 text-xs rounded-full gap-1 transition-colors",
                internationalPlanning 
                  ? "border-primary bg-primary/10 text-foreground hover:bg-primary/15" 
                  : "text-muted-foreground border-border/50 hover:text-foreground hover:bg-muted/50 active:bg-muted"
              )}
            >
              {internationalPlanning && <Check className="h-3 w-3" />}
              <span className="whitespace-nowrap">{lCommon('labels.international', { defaultValue: 'International' })}</span>
            </Button>
          </div>

          {/* Right side: Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={chatText.trim().length === 0}
            size="icon"
            aria-label={lCommon('aria.submit_message', { defaultValue: 'This button submits your message.' })}
            className="flex-shrink-0 h-9 w-9 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg disabled:shadow-none active:scale-95"
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
